import React, { useContext, useEffect, useState } from "react";

import { ButtonNova } from "@/components/ui-kit/ButtonNova/ButtonNova";
import LoadingSpinner from "@/components/ui-kit/Loading/LoadingSpinner/LoadingSpinner";
import Picture from "@/components/ui-kit/Picture/Picture";
import { TextElement } from "@/components/ui-kit/TextElement/TextElement";
import SearchContext from "@/context/SearchContext";
import { useNavigation } from "@/hooks/useNavigation";
import type { ContentfulProduct } from "@/types/common";
import type { LinkedItem } from "@/types/search";
import { setCookie } from "@/utils/cookies";
import { GA4 } from "@/utils/ga4Utils";

import LinkedItems from "./LinkedItems";
import ProductList from "./ProductList";
import SearchResultsLayout from "./SearchResultsLayout";

interface ProductSearchProps {
  data: {
    imageId?: string;
    shortDescription?: string;
    slug?: string;
    objectID?: string;
    title?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
  };
  onClick: (event: React.MouseEvent<HTMLButtonElement>, item: LinkedItem) => void;
  queryID?: string;
}

const ProductSearch: React.FC<ProductSearchProps> = ({ data, onClick, queryID }) => {
  const { searchState, searchClient, contentfulProducts, search, closeSearchDropdown } =
    useContext(SearchContext);

  const [loading, setLoading] = useState(true);
  const [relatedFAQs, setRelatedFAQs] = useState<LinkedItem[]>([]);
  const navigate = useNavigation();

  // Transform Contentful products once per render (memoize if needed)
  const allProducts: Record<string, ContentfulProduct> = transformContentfulData(
    contentfulProducts ?? []
  );

  const product = data?.imageId ? allProducts[data.imageId] : undefined;
  const relatedProducts = product?.relatedProducts ?? [];

  useEffect(() => {
    let mounted = true;

    const fetchRelatedFAQs = async () => {
      if (searchClient && data?.shortDescription) {
        try {
          const relatedFAQsResult = await searchClient.searchSingleIndex({
            indexName: process.env.NEXT_PUBLIC_ALGOLIA_ZENDESK_INDEX_NAME ?? "Zendesk_production",
            searchParams: {
              query: data?.shortDescription,
              hitsPerPage: 5,
            },
          });
          if (mounted) {
            setRelatedFAQs(relatedFAQsResult?.hits || []);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(`Algolia API error related articles ${error}`);
        } finally {
          if (mounted) setLoading(false);
        }
      } else {
        if (mounted) setLoading(false);
      }
    };
    fetchRelatedFAQs();

    return () => {
      mounted = false;
    };
  }, [data, searchClient]);

  if (loading || !product) {
    return (
      <div className="relative w-full h-full">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <SearchResultsLayout>
      <div className="flex flex-col product-container">
        <TextElement
          text="Recommended"
          className={`title--small !text-[14px] !leading-[1.3] md:!text-[18px] xl:!text-[24px] mb-2 xl:mb-4`}
        />
        <div
          className="flex gap-4 md:gap-6 mt-[-4px] md:mt-0 items-center flex-wrap cursor-pointer"
          onClick={() => {
            GA4.onSearchClicked({
              search_term: search,
              destination: product.slug,
              name: "Search",
              location: "navigation",
              state: searchState,
              text: product.title,
              type: "Link",
              index: "Products",
              eventType: "click",
              queryID,
              objectID: product?.objectID,
            });
            setCookie("queryID", product?.queryID ?? "");
            closeSearchDropdown?.(false);
            if (product.slug) navigate(product.slug);
          }}
          role="link"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (product.slug) navigate(product.slug);
            }
          }}
          aria-label={product.title ?? "Product"}
        >
          <div className="img-container w-[50%] md:w-[188px]">
            {product.image && (
              <Picture
                src={product.image}
                className="rounded-lg object-cover w-full h-full"
                alt={product.title ?? "Product Image"}
                width={188}
                height={188}
              />
            )}
          </div>
          <div className="description-container flex-1 text-left">
            <TextElement
              text={product.title ?? ""}
              className="body--xlarge-b product-title !mb-1 lg:text-nova-brand-primary-80"
            />
            <TextElement
              text={product.shortDescription ?? ""}
              className="hidden md:block body--regular mb-1"
            />

            <ButtonNova level="Textlink" label="Shop Now" icon="Right" />
          </div>
          <div className="mobile-description md:hidden w-100 mt-[-8px]">
            <TextElement
              text={product.shortDescription ?? ""}
              className="body--regular mb-1 text-left"
            />
          </div>
        </div>

        {relatedProducts.length > 0 && (
          <div className="below-recommended">
            <div className="mb-4 w-[calc(100%+16px)] md:w-[100%]">
              <TextElement
                text="You might also be interested in..."
                className="body--large-b mt-8 mb-1 md:mb-2"
              />
              <div className="recommended-container mb-4 md:mb-10">
                <ProductList products={relatedProducts.slice(0, 4)} />
              </div>
            </div>
          </div>
        )}
      </div>

      <div>
        <LinkedItems title="Related" items={relatedFAQs} onClick={onClick} />
      </div>
    </SearchResultsLayout>
  );
};

// Helper to transform product array into object by imageId
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function transformContentfulData(products: any[] = []) {
  const map: Record<string, ContentfulProduct> = {};
  products.forEach((item) => {
    const imageUrl = item?.searchImage?.file?.url;
    const imageId = imageUrl?.split("/")[4];

    if (imageId) {
      // @ts-expect-error - this is a workaround for a bug in the browser
      map[imageId] = {
        image: imageUrl,
        title: item.title,
        slug: item.urlPath,
        subTitle: item.subTitle,
        shortDescription: item.shortDescription,
        description: item.longDescription,
        relatedProducts: item.relatedProducts || [],
        queryID: item.queryID,
        objectID: item.sys?.id,
      };
    }
  });
  return map;
}

export default ProductSearch;
